import { __assign, __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useState } from "react";
import { InputContainer, InputWrapper } from "./styles";
import { Controller } from "react-hook-form";
import { useTheme } from "styled-components";
import { Spinner } from "../Spinner";
import { IconExclamation, IconEye, IconEyeSlash } from "../icons";
import { NumberFormatBase } from "react-number-format";
import { currencyFormatter } from "../../utils/functions";
import { FieldInfo } from "../FieldInfo";
var maskValue = function (value, mask) {
    var maskedValue = "";
    var valueIndex = 0;
    var letterRegex = /^[a-zA-ZÀ-ÿ~^´`]+$/;
    for (var i = 0; i < mask.length; i++) {
        var maskChar = mask[i];
        var inputChar = value[valueIndex];
        if (!inputChar)
            break;
        if (maskChar === "9") {
            if (/\d/.test(inputChar)) {
                maskedValue += inputChar;
                valueIndex++;
            }
            else {
                break;
            }
        }
        else if (maskChar === "a") {
            if (letterRegex.test(inputChar)) {
                maskedValue += inputChar;
                valueIndex++;
            }
            else {
                break;
            }
        }
        else if (maskChar === "*") {
            maskedValue += inputChar;
            valueIndex++;
        }
        else {
            maskedValue += maskChar;
            if (inputChar === maskChar) {
                valueIndex++;
            }
        }
    }
    return maskedValue;
};
export var Input = forwardRef(function (_a, ref) {
    var _b;
    var label = _a.label, info = _a.info, error = _a.error, isLoading = _a.isLoading, register = _a.register, mask = _a.mask, containerStyles = _a.containerStyles, isCurrency = _a.isCurrency, hasPrefix = _a.hasPrefix, minimumFractionDigits = _a.minimumFractionDigits, control = _a.control, iconLeft = _a.iconLeft, iconRight = _a.iconRight, props = __rest(_a, ["label", "info", "error", "isLoading", "register", "mask", "containerStyles", "isCurrency", "hasPrefix", "minimumFractionDigits", "control", "iconLeft", "iconRight"]);
    var theme = useTheme();
    var spinnerSize = parseInt(theme.components.input.input.fontSize[theme.device].substring(-2)) + 4;
    var _c = useState(false), passwordShowing = _c[0], setPasswordShowing = _c[1];
    var togglePasswordVisibility = function () {
        setPasswordShowing(function (prev) { return !prev; });
    };
    var isPasswordField = props.type === "password";
    var inputType = isPasswordField && passwordShowing ? "text" : props.type;
    var passwordIcon = isPasswordField ? (passwordShowing ? (_jsx(IconEyeSlash, { style: { cursor: "pointer" }, onClick: togglePasswordVisibility })) : (_jsx(IconEye, { style: { cursor: "pointer" }, onClick: togglePasswordVisibility }))) : (iconRight);
    return (_jsxs(InputWrapper, { className: props.className, style: __assign({ width: (_b = props.style) === null || _b === void 0 ? void 0 : _b.width }, containerStyles), children: [label && _jsx("label", { children: label }), _jsxs(InputContainer, { "$hasIconLeft": !!iconLeft, "$hasIconRight": !!passwordIcon, "$hasError": !!error, children: [iconLeft && _jsx("div", { className: "icon-left", children: iconLeft }), isCurrency ? (_jsx(CurrencyInput, __assign({ control: control, hasPrefix: hasPrefix, minimumFractionDigits: minimumFractionDigits }, props, { type: inputType, ref: ref }))) : (_jsx(NormalInput, __assign({ isLoading: isLoading }, props, { type: inputType, ref: ref, register: register, onChange: function (e) {
                            var _a;
                            if (mask) {
                                e.target.value = maskValue(e.target.value, mask);
                            }
                            (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, e);
                        } }))), isLoading ? (_jsx(Spinner, { size: spinnerSize, color: theme.colors.neutral.light, style: {
                            position: "absolute",
                            right: 16,
                            top: "calc(50% - ".concat(spinnerSize / 2, "px)"),
                        } })) : error ? (_jsx(IconExclamation, { className: "error-icon" })) : (passwordIcon && _jsx("div", { className: "icon-right", children: passwordIcon }))] }), (!!register || !!error || !!info) && (_jsx(FieldInfo, { isDisabled: props.disabled || isLoading, isError: !!error, children: error || info }))] }));
});
var NormalInput = forwardRef(function (_a, ref) {
    var isLoading = _a.isLoading, register = _a.register, props = __rest(_a, ["isLoading", "register"]);
    return (_jsx("input", __assign({}, props, { disabled: isLoading || props.disabled }, register, { ref: function (e) {
            if (register) {
                register.ref(e);
            }
            if (ref) {
                //@ts-ignore
                ref.current = e;
            }
        } })));
});
var CurrencyInput = forwardRef(function (_a, ref) {
    var control = _a.control, name = _a.name, hasPrefix = _a.hasPrefix, _b = _a.minimumFractionDigits, minimumFractionDigits = _b === void 0 ? 2 : _b, value = _a.value, register = _a.register, isLoading = _a.isLoading, props = __rest(_a, ["control", "name", "hasPrefix", "minimumFractionDigits", "value", "register", "isLoading"]);
    var multiplicator = Math.pow(10, minimumFractionDigits);
    return control ? (_jsx(Controller, { name: name || "", control: control, render: function (_a) {
            var field = _a.field;
            return (_jsx(NumberFormatBase, __assign({ format: function (e) {
                    return currencyFormatter(parseFloat(e), hasPrefix, minimumFractionDigits);
                }, value: Number(value) * multiplicator }, props, { onValueChange: function (e) {
                    var _a;
                    (_a = field.onChange) === null || _a === void 0 ? void 0 : _a.call(field, e.floatValue ? e.floatValue / multiplicator : undefined);
                }, defaultValue: props.defaultValue, disabled: isLoading || props.disabled, type: "text", getInputRef: ref })));
        } })) : (_jsx(NumberFormatBase, __assign({ format: function (e) {
            return currencyFormatter(parseFloat(e), hasPrefix, minimumFractionDigits);
        } }, props, { value: Number(value) * multiplicator, defaultValue: props.defaultValue, disabled: isLoading || props.disabled, onChange: undefined, onValueChange: function (e) {
            var _a;
            return (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, {
                target: {
                    //@ts-ignore
                    value: e.floatValue ? e.floatValue / multiplicator : undefined,
                },
            });
        }, type: "text", getInputRef: ref })));
});
//# sourceMappingURL=index.js.map